import { ArrowDown, ArrowUp } from 'lucide-react';
import { cn } from '../../lib/utils';
import { formatDate, formatMoney, formatNumber, formatPercent } from '../../lib/format';
import {
  METRICS,
  TABLE_METRICS,
  metricDelta,
  metricValue,
  nextSort,
  sortRows,
  type MetricKey,
  type Metrics,
  type ReportRow,
  type RowAction,
  type SortKey,
  type SortState,
} from './aggregate';

export function formatMetric(m: Metrics, key: MetricKey, currency: string): string {
  const v = metricValue(m, key);
  const f = METRICS[key].format;
  if (f === 'percent') return formatPercent(v);
  if (f === 'money') return formatMoney(v, currency);
  return formatNumber(Math.round(v));
}

/** The change under a number; nothing when compare is off. */
export function DeltaText({
  metric,
  cur,
  prev,
  className,
}: {
  metric: MetricKey;
  cur: Metrics;
  prev: Metrics | null;
  className?: string;
}) {
  if (!prev) return null;
  const d = metricDelta(metric, cur, prev);
  return (
    <span
      className={cn(
        'text-xs font-semibold whitespace-nowrap',
        d.tone === 'good' && 'text-success-text',
        d.tone === 'bad' && 'text-danger-text',
        (d.tone === 'flat' || d.tone === 'neutral') && 'text-muted font-medium',
        className,
      )}
    >
      {d.text}
    </span>
  );
}

const KPI_TILES: { key: MetricKey; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'openRate', label: 'Open rate' },
  { key: 'clickRate', label: 'Click rate' },
  { key: 'orders', label: 'Orders' },
  { key: 'revenue', label: 'Revenue' },
  { key: 'unsubRate', label: 'Unsubscribed' },
  { key: 'bounceRate', label: 'Bounce rate' },
];

export function KpiTiles({
  cur,
  prev,
  currency,
}: {
  cur: Metrics;
  prev: Metrics | null;
  currency: string;
}) {
  const bounce = metricValue(cur, 'bounceRate');
  return (
    <div
      role="list"
      aria-label="Key numbers"
      className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] max-sm:grid-cols-2 gap-px overflow-hidden rounded-[14px] border border-border bg-card"
    >
      {KPI_TILES.map(({ key, label }) => (
        <div
          role="listitem"
          key={key}
          className="flex min-w-0 flex-col gap-0.5 bg-card px-4 py-3.5 shadow-[0_0_0_1px_var(--color-border)]"
        >
          <span className="text-xs text-muted">{label}</span>
          <span className="text-[26px] font-semibold tracking-tight tabular-nums">
            {formatMetric(cur, key, currency)}
          </span>
          <DeltaText metric={key} cur={cur} prev={prev} />
          {key === 'openRate' ? (
            <span className="text-xs text-faint">
              bot opens not counted: {formatNumber(cur.botOpened)}
            </span>
          ) : null}
          {key === 'unsubRate' ? (
            <span className="text-xs text-faint">{formatNumber(cur.unsubscribed)} people</span>
          ) : null}
          {key === 'bounceRate' ? (
            <span
              className={cn('text-xs', bounce < 0.02 ? 'text-success-text' : 'text-danger-text')}
            >
              {bounce < 0.02 ? '✓' : '✗'} limit 2%
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

const FUNNEL: { key: keyof Metrics; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'opened', label: 'Opened' },
  { key: 'clicked', label: 'Clicked' },
  // Orders attributed to the mails in the period (Mikkel 26/10).
  { key: 'orders', label: 'Ordered' },
];

export function Funnel({ cur, prev }: { cur: Metrics; prev: Metrics | null }) {
  const showPrev = !!prev && prev.sent > 0;
  const max = Math.max(cur.sent, showPrev ? prev.sent : 0) || 1;
  return (
    <div className="flex flex-col gap-2.5">
      <ol className="m-0 p-0 list-none flex flex-col gap-2.5">
        {FUNNEL.map((step, i) => {
          const v = cur[step.key];
          const before = i > 0 ? cur[FUNNEL[i - 1]!.key] : 0;
          return (
            <li
              key={step.key}
              className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2.5 gap-y-1 text-[13px]"
            >
              <span>{step.label}</span>
              <div
                className="relative col-span-2 row-start-2 h-[22px] rounded-md bg-bg"
                aria-hidden="true"
              >
                {showPrev ? (
                  <span
                    className="absolute inset-y-0 left-0 rounded-md border-[1.5px] border-dashed border-zinc-400"
                    style={{ width: `${(prev[step.key] / max) * 100}%` }}
                  />
                ) : null}
                <span
                  className="absolute inset-y-1 left-0 rounded bg-accent"
                  style={{ width: `${Math.max(0.4, (v / max) * 100)}%` }}
                />
              </div>
              <span className="text-right tabular-nums">
                <b className="font-semibold">{formatNumber(v)}</b>
                {showPrev ? (
                  <span className="sr-only">, compared with {formatNumber(prev[step.key])}</span>
                ) : null}
                <small className="ml-1.5 text-[11px] text-faint">
                  {i > 0 ? `${formatPercent(before > 0 ? v / before : 0)} of previous` : ''}
                </small>
              </span>
            </li>
          );
        })}
      </ol>
      <div className="flex flex-wrap gap-3.5 text-xs text-muted">
        <span>
          <i className="inline-block w-3.5 h-2 rounded-sm bg-accent mr-1.5 align-middle" />
          Period
        </span>
        {showPrev ? (
          <span>
            <i className="inline-block w-3.5 h-2 rounded-sm border-[1.5px] border-dashed border-zinc-400 mr-1.5 align-middle" />
            Compared with
          </span>
        ) : null}
      </div>
    </div>
  );
}

function SortHeader({
  sortKey,
  label,
  sort,
  onSort,
  align = 'right',
}: {
  sortKey: SortKey;
  label: string;
  sort: SortState;
  onSort: (s: SortState) => void;
  align?: 'left' | 'right';
}) {
  const active = sort.key === sortKey;
  return (
    <th
      scope="col"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cn(
        'px-2.5 py-2 bg-bg text-xs font-medium text-muted whitespace-nowrap',
        align === 'left' ? 'text-left' : 'text-right',
      )}
    >
      <button
        type="button"
        onClick={() => onSort(nextSort(sort, sortKey))}
        className={cn(
          'inline-flex items-center gap-1 hover:text-text rounded-sm focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2',
          active && 'text-text font-semibold',
        )}
      >
        {label}
        {active ? (
          sort.dir === 'asc' ? (
            <ArrowUp size={12} aria-hidden="true" />
          ) : (
            <ArrowDown size={12} aria-hidden="true" />
          )
        ) : null}
      </button>
    </th>
  );
}

function actionLabel(row: ReportRow): string {
  if (row.action?.kind === 'flow') return `Show the emails in ${row.name}`;
  if (row.action?.kind === 'campaign') return `Open ${row.name}`;
  return `Show all campaigns`;
}

export function ReportTable({
  rows,
  sort,
  onSort,
  onAction,
  currency,
  total,
}: {
  rows: ReportRow[];
  sort: SortState;
  onSort: (s: SortState) => void;
  onAction: (a: RowAction) => void;
  currency: string;
  /** Shown as a "Total" footer row when given. */
  total?: { cur: Metrics; prev: Metrics | null };
}) {
  const sorted = sortRows(rows, sort);
  return (
    <div className="overflow-x-auto rounded-xl border border-border">
      <table className="w-full min-w-[820px] border-collapse text-[13px]">
        <thead>
          <tr>
            <SortHeader sortKey="name" label="Name" sort={sort} onSort={onSort} align="left" />
            {TABLE_METRICS.map((k) => (
              <SortHeader
                key={k}
                sortKey={k}
                label={METRICS[k].label}
                sort={sort}
                onSort={onSort}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.id} className="border-t border-border hover:bg-hover">
              <th scope="row" className="px-2.5 py-2 text-left font-normal whitespace-nowrap">
                {row.action ? (
                  <button
                    type="button"
                    onClick={() => row.action && onAction(row.action)}
                    aria-label={actionLabel(row)}
                    className="font-medium text-link hover:underline rounded-sm focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2"
                  >
                    {row.name}
                  </button>
                ) : (
                  <span className="font-medium">{row.name}</span>
                )}
                <span className="ml-1.5 rounded-full border border-border px-1.5 py-px text-[11px] text-muted">
                  {row.type}
                </span>
                {row.sentAt ? (
                  <span className="ml-1.5 text-[11px] text-faint">{formatDate(row.sentAt)}</span>
                ) : null}
              </th>
              {TABLE_METRICS.map((k) => (
                <td key={k} className="px-2.5 py-2 text-right whitespace-nowrap tabular-nums">
                  {formatMetric(row.cur, k, currency)}
                  <DeltaText
                    metric={k}
                    cur={row.cur}
                    prev={row.prev}
                    className="block text-[11px] font-medium"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {total ? (
          <tfoot>
            <tr className="border-t-2 border-border bg-bg font-semibold">
              <th scope="row" className="px-2.5 py-2 text-left">
                Total
              </th>
              {TABLE_METRICS.map((k) => (
                <td key={k} className="px-2.5 py-2 text-right whitespace-nowrap tabular-nums">
                  {formatMetric(total.cur, k, currency)}
                  <DeltaText
                    metric={k}
                    cur={total.cur}
                    prev={total.prev}
                    className="block text-[11px] font-medium"
                  />
                </td>
              ))}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
